import { useTheme } from '@flowcore-water/ui';

interface DataTableProps {
  columns: string[];
  rows: Record<string, unknown>[];
  sortColumn?: string;
  sortOrder?: string;
  onSort?: (column: string) => void;
  onRowClick?: (row: Record<string, unknown>) => void;
  /** Columns to hide from the table (e.g., raw, custom_fields) */
  hideColumns?: string[];
}

/** Format a cell value for display. */
function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  const s = String(value);
  // Truncate long strings
  return s.length > 80 ? s.slice(0, 77) + '...' : s;
}

/** Make column header human-readable. */
function formatHeader(col: string): string {
  return col
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const HIDDEN_BY_DEFAULT = new Set([
  'raw', 'custom_fields', 'tag_type_ids', 'zone_ids', 'role_ids',
  'items', 'tags', 'reason', 'synced_at',
]);

export function DataTable({
  columns,
  rows,
  sortColumn,
  sortOrder,
  onSort,
  onRowClick,
  hideColumns = [],
}: DataTableProps) {
  const { t } = useTheme();
  const hidden = new Set([...HIDDEN_BY_DEFAULT, ...hideColumns]);
  const visibleCols = columns.filter((c) => !hidden.has(c));

  return (
    <div className="overflow-x-auto rounded-lg border" style={{ borderColor: t.border }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: t.tableHeaderBg }}>
            {visibleCols.map((col) => (
              <th
                key={col}
                className="whitespace-nowrap px-3 py-2.5 text-left text-xs font-mono uppercase tracking-wide cursor-pointer select-none"
                style={{ color: t.sectionHeading }}
                onClick={() => onSort?.(col)}
              >
                {formatHeader(col)}
                {sortColumn === col && (
                  <span className="ml-1">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={visibleCols.length}
                className="px-3 py-8 text-center"
                style={{ color: t.textMuted }}
              >
                No records found
              </td>
            </tr>
          )}
          {rows.map((row, i) => (
            <tr
              key={String(row.id ?? i)}
              className="transition-colors"
              style={{
                borderTop: `1px solid ${t.borderSubtle}`,
                cursor: onRowClick ? 'pointer' : undefined,
              }}
              onClick={() => onRowClick?.(row)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = t.surfaceHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {visibleCols.map((col) => (
                <td
                  key={col}
                  className="whitespace-nowrap px-3 py-2"
                  style={{ color: t.textSecondary }}
                >
                  {formatCell(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
